/**
 * Explanations for the codes an upload problem carries: the engine's blocking issues, the browser
 * worker's and the transfer's failures, and the server's rejections (UPL-06, UPL-11). The engine
 * knows about a hundred codes; they fall into a few groups the uploader can act on. The code
 * itself is still shown, for support.
 */
import type { MessageKey } from '../lib/i18n'

// Ordered: the first matching rule explains a code.
const RULES: readonly (readonly [RegExp, MessageKey])[] = [
  [/^part10_file_required$/, 'why_not_dicom'],
  [/^(unreadable|zip_.*)$/, 'why_unreadable'],
  [/^burned_in_annotation$/, 'why_burned_in'],
  [/^(left_out_in_review|review_changed|review_mismatch)$/, 'why_review'],
  [/^(unsupported_(sop_class|transfer_syntax|pixel_layout)|pixel_data_missing|signed_pixels_need_jpeg_lossless_source|unused_pixel_bits)$/, 'why_unsupported'],
  [/^(file_missing|size_mismatch|transfer_failed.*)$/, 'why_transfer'],
  [/^(invalid_jpeg_stream|pixel_decoder_failed|encoder_failed|pixel_round_trip_failed|pixel_dimensions_mismatch|(pixel_review_|pixel_|content_)digest_mismatch|name_mismatch)$/, 'why_damaged'],
  [/^patient_identity_missing_or_invalid$/, 'why_identity'],
  [/^patient_id_form$/, 'why_patient_id_form'],
  [/^site_setup_required$/, 'why_site_setup'],
  [/^token_(setup_)?required$/, 'why_token_required'],
  [/^key_not_enrolled$/, 'why_key_not_enrolled'],
  [/^key_other_site$/, 'why_key_other_site'],
  [/^unresolved_instance_reference$/, 'why_reference_unresolved'],
  [/^(cyclic_or_unresolved_reference|reference_revision_selection_required)$/, 'why_reference'],
  [/^site_agreement_invalid$/, 'why_agreement'],
  [/^foreign_anonymization_record$/, 'why_foreign'],
  [/^unsafe_(file_meta|file_preamble|private_attribute|attribute|pixel_data)$/, 'why_unsafe_metadata'],
  [/^unsupported_presentation$/, 'why_presentation'],
  [/^restricted_characteristic_retained$/, 'why_retained_characteristic'],
  [/^(not_anonymized|deidentification_method_missing|(invalid_)?private_.*)$/, 'why_record'],
  [/^annotation_label_requires_review$/, 'why_foreign_labels'],
  [/annotation/, 'why_annotations'],
  [/_conflict$/, 'why_conflict'],
  [/(_missing|_unknown|_needs_resolution|^unrecognized_|^required_|^timestamp_fallback$)/, 'why_missing_value'],
  [/^invalid_/, 'why_invalid_value'],
  // The DICOM validator's findings in the file the release would write, e.g. iod_TagEmpty.
  [/^iod_Tag(Missing|Empty)$/, 'why_missing_value'],
  [/^iod_/, 'why_invalid_value'],
  [/^validation_failed$/, 'why_validation_failed'],
  [/^worker_failed/, 'why_worker'],
  [/^(interrupted|cancelled)$/, 'why_interrupted'],
  [/^token_no_match$/, 'tokenNoMatch'],
  [/^nothing_chosen$/, 'noFilesFound'],
  // Not problems: files the platform holds already (UPL-04, UPL-07).
  [/^sent_before$/, 'why_sent_before'],
  [/^duplicate$/, 'why_duplicate'],
]

/**
 * Problems the uploader can correct in the browser (ANON-06): patient ID, side or body part, labels the
 * catalog lacks (UPL-09), and references to images never uploaded.
 */
const CORRECTABLE: ReadonlySet<MessageKey> = new Set(['why_identity', 'why_patient_id_form', 'why_missing_value', 'why_conflict', 'why_foreign_labels', 'why_reference_unresolved'])
/** Files that are not for the platform: nothing to do about them. */
const NOT_FOR_UPLOAD: ReadonlySet<MessageKey> = new Set(['why_not_dicom', 'why_unsupported', 'why_burned_in'])

/** What the uploader can do about a problem: correct it here, nothing (the file is not for the platform), or look into it. */
export type Remedy = 'correct' | 'none' | 'investigate'

/** What can be done about a file, by all its problems: one that is not for the platform is left out whatever else it has. */
export function remedy(reason: string | undefined): Remedy {
  const keys = explanations(reason).map(([key]) => key)
  if (keys.some((key) => NOT_FOR_UPLOAD.has(key))) return 'none'
  return keys.length > 0 && keys.every((key) => CORRECTABLE.has(key)) ? 'correct' : 'investigate'
}

/** The message that explains one code. */
export function explain(code: string): MessageKey {
  return RULES.find(([pattern]) => pattern.test(code))?.[1] ?? 'why_other'
}

/** A problem's explanations, each with the codes it covers; the check joins a file's issues with commas. */
export function explanations(reason: string | undefined): [MessageKey, string[]][] {
  const grouped = new Map<MessageKey, string[]>()
  for (const code of reason ? reason.split(', ') : []) {
    const key = explain(code)
    grouped.set(key, [...(grouped.get(key) ?? []), code])
  }
  return [...grouped]
}
